import { type TeamStatus, useFormatter, useIntl, WithSkeleton } from '@capsicum/common';
import { CondensedButton, Image, SkeletonItem, Text } from '@capsicum/common-web';
import styled from 'styled-components';

import { useWCPledgeCancelModal } from '../../hooks/modals/useWCPledgeCancelModal';
import { useStakeMore } from '../../hooks/useStakeMore';

export type WCPledgeUserPledgeProps = {
	cancelPledgeModalTexts: {
		primaryButtonText: string;
		secondaryButtonText: string;
		subtitle: string;
		title: string;
	};
	chzAmount: string;
	fiatAmount?: string;
	ftName: string;
	stakeMoreButtonText: string;
	teamFlagUrl: string;
	teamId: string;
	teamName: string;
	teamStatus: TeamStatus;
	unstakeButtonText: string;
};

export const WCPledgeUserPledgeItemComponent = ({
	cancelPledgeModalTexts,
	chzAmount,
	fiatAmount = '',
	ftName,
	stakeMoreButtonText,
	teamFlagUrl,
	teamId,
	teamName,
	teamStatus,
	unstakeButtonText
}: WCPledgeUserPledgeProps) => {
	const unstakeInfo = {
		chzAmount,
		ftName,
		teamFlagUrl,
		teamId,
		teamName
	};
	const { showCancelPledgeModal } = useWCPledgeCancelModal(unstakeInfo, cancelPledgeModalTexts);
	const { handleStakeMore } = useStakeMore();
	const { formatCrypto } = useFormatter();
	const { $t } = useIntl();

	return (
		<S.PledgeItem data-testid={`wcpledge-user-pledge-item-${teamName.toLowerCase()}`}>
			<S.PledgeItemTopSection>
				<S.TeamInfo>
					<Image alt={`${teamName} flag`} height={20} src={teamFlagUrl} width={20} />
					<S.TeamNameContainer>
						<Text variant="text2">{teamName}</Text>
						{teamStatus === 'Failed' && (
							<Text variant="caption1">
								{$t('worldChampionshipPledge.activityTab.yourPledgeSection.notQualifiedText')}
							</Text>
						)}
					</S.TeamNameContainer>
				</S.TeamInfo>
				<S.AmountInfo>
					<Text testID={`wcpledge-${teamName.toLowerCase()}-chz-pledged-amount`} variant="text2">
						{`${formatCrypto(chzAmount, 'CHZ')}`}
					</Text>
					<Text testID={`wcpledge-${teamName.toLowerCase()}-fiat-pledged-amount`} variant="caption2">
						{`≈${formatCrypto(fiatAmount, 'USD')}`}
					</Text>
				</S.AmountInfo>
			</S.PledgeItemTopSection>
			<S.ButtonsContainer>
				<S.StyledButton
					color="brand"
					size="xsmall"
					testID={`wcpledge-${teamName.toLowerCase()}-cancel-pledge-button`}
					text={unstakeButtonText}
					textSize="xsmall"
					variant="outlined"
					onClick={showCancelPledgeModal}
				/>
				<S.StyledButton
					color="brand"
					disabled={teamStatus === 'Failed'}
					size="xsmall"
					testID={`wcpledge-${teamName.toLowerCase()}-stake-more-pledge-button`}
					text={stakeMoreButtonText}
					textSize="xsmall"
					onClick={handleStakeMore(teamId, ftName)}
				/>
			</S.ButtonsContainer>
		</S.PledgeItem>
	);
};

export const WCPledgeUserPledgeItemSkeleton = () => (
	<S.PledgeItem>
		<S.PledgeItemTopSection>
			<S.TeamInfo>
				<SkeletonItem borderRadius={2} height={20} width={20} />
				<SkeletonItem borderRadius={2} height={20} width={100} />
			</S.TeamInfo>
			<S.AmountInfo>
				<SkeletonItem borderRadius={2} height={20} width={100} />
			</S.AmountInfo>
		</S.PledgeItemTopSection>
	</S.PledgeItem>
);

export const WCPledgeUserPledgeItem = WithSkeleton(WCPledgeUserPledgeItemComponent, WCPledgeUserPledgeItemSkeleton);

export namespace S {
	export const PledgeItem = styled.div`
		display: flex;
		flex-direction: column;
		justify-content: space-between;
		border-top: 1px solid ${({ theme }) => theme.colors.borders.main.subtle};
		padding: ${({ theme }) => theme.spacings.xxsmall} 0;
		gap: ${({ theme }) => theme.spacings.small};
	`;

	export const PledgeItemTopSection = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: space-between;
	`;

	export const TeamInfo = styled.div`
		display: flex;
		align-items: flex-start;
		gap: ${({ theme }) => theme.spacings.xsmall};
		flex: 1;
	`;

	export const TeamNameContainer = styled.div`
		display: flex;
		flex-direction: column;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const AmountInfo = styled.div`
		display: flex;
		flex-direction: column;
		align-items: flex-end;
		gap: ${({ theme }) => theme.spacings.xxsmall};
	`;

	export const ButtonsContainer = styled.div`
		display: flex;
		flex-direction: row;
		justify-content: flex-end;
		gap: ${({ theme }) => theme.spacings.xsmall};
	`;

	export const StyledButton = styled(CondensedButton)`
		align-self: flex-end;
	`;
}
